import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react';

import { CapacityMeta, CapacityWarning, useCapacity } from '../components/Capacity';
import { GitHubCard } from '../components/GitHub';
import { Callout, CopyAction, CopyButton, Empty, ErrorCallout, InlineConfirm, Lamp, PageHead, Segmented, SkeletonRows, StatePill } from '../components/ui';
import { api } from '../lib/api';
import { ago, clock, duration, fullTime, moneyFine, repoName, shortId, span } from '../lib/format';
import { useAction, useDocumentTitle, useNow, useResource } from '../lib/hooks';
import type { CreatedBy, FailureReason, Task, TaskEvent, TaskState, TaskSummary, TeamDetail } from '../lib/types';
import { ReplayChip, ReplayLink } from './Replay';

const PAGE = 25;
const STATES: { value: TaskState | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'timed_out', label: 'Timed out' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const isTerminal = (s: TaskState) => s === 'succeeded' || s === 'failed' || s === 'timed_out' || s === 'cancelled';

export function taskDuration(task: { startedAt?: number; endedAt?: number }, now: number): number | null {
  if (task.startedAt === undefined) return null;
  return (task.endedAt ?? now) - task.startedAt;
}

/** The key's name, or the person's email when someone started it from the console. */
export function createdByLabel(work: { createdBy?: CreatedBy | null }): string {
  if (!work.createdBy) return '—';
  return 'keyName' in work.createdBy ? work.createdBy.keyName : work.createdBy.email;
}

/** Failures the fleet caused, or that never ran: none of them is billed. */
const UNBILLED: FailureReason[] = ['no-capacity', 'box-lost', 'plane-restart', 'never-started'];
export const isUnbilled = (task: { failureReason?: FailureReason | null }) => !!task.failureReason && UNBILLED.includes(task.failureReason);

/** Why a failed or timed-out task ended, in a few plain words. */
export function failureLabel(task: { failureReason?: FailureReason | null; exitCode: number | null }): string | null {
  switch (task.failureReason) {
    case 'exit':
      return `Command exited ${task.exitCode ?? 'non-zero'}`;
    case 'setup':
      return task.exitCode === 0 ? 'Exited 0 without its artifacts' : 'Couldn’t clone or start';
    case 'timeout':
      return 'Ran past its timeout';
    case 'no-capacity':
      return 'No Mac was free (not billed)';
    case 'box-lost':
      return 'Lost by the fleet (not billed)';
    case 'plane-restart':
      return 'Plane restarted (not billed)';
    case 'never-started':
      return 'Never started (not billed)';
    default:
      return null;
  }
}

/** A failure reason under the state pill; "(not billed)" never breaks across lines. */
function ReasonText({ label }: { label: string }) {
  const unbilled = label.endsWith(' (not billed)');
  return (
    <span className="cell-sub m-sub">
      {unbilled ? label.slice(0, -' (not billed)'.length) : label}
      {unbilled ? (
        <>
          {' '}
          <span className="nowrap">(not billed)</span>
        </>
      ) : null}
    </span>
  );
}

/** GitHub's verdict on a job, in the same pills as a task's state. */
const CONCLUSIONS: Record<string, { lamp: string; label: string; pill?: string }> = {
  success: { lamp: 'lamp--accent', label: 'Passed' },
  failure: { lamp: 'lamp--refused', label: 'Failed', pill: 'pill--failed' },
  cancelled: { lamp: 'lamp--off', label: 'Cancelled', pill: 'pill--muted' },
  timed_out: { lamp: 'lamp--magenta', label: 'Timed out' },
};

/** A GitHub job's conclusion once it has one, else the task's own state. */
function WorkState({ task }: { task: TaskSummary }) {
  const conclusion = task.github?.conclusion;
  if (!conclusion) return <StatePill state={task.state} />;
  const spec = CONCLUSIONS[conclusion] ?? { lamp: 'lamp--off', label: conclusion.charAt(0).toUpperCase() + conclusion.slice(1).replace(/_/g, ' '), pill: 'pill--muted' };
  return (
    <span className={`pill ${spec.pill ?? ''}`} title={`GitHub: ${conclusion}`}>
      <span className={`lamp ${spec.lamp}`} aria-hidden="true" />
      {spec.label}
    </span>
  );
}

/** Under the cost: whether it is billed. A GitHub job's Mac is billed only once GitHub hands it the job; one that got none costs nothing. */
function costNote(task: Task): string {
  if (isUnbilled(task)) return 'not billed';
  if (task.spec.kind === 'runner' && task.github?.jobStartedAt === undefined) {
    return isTerminal(task.state) ? 'not billed: GitHub gave it no job' : 'billed once GitHub hands it a job';
  }
  return isTerminal(task.state) ? 'billed per second' : 'accruing per second';
}

/** Cursor pagination, kept in the URL so back and reload land on the same page. */
function useCursorPages() {
  const [params, setParams] = useSearchParams();
  const cursors = (params.get('after') ?? '').split(',').filter(Boolean);
  const cursor = cursors[cursors.length - 1] ?? null;
  const go = (next: string[]) => {
    const p = new URLSearchParams(params);
    if (next.length) p.set('after', next.join(','));
    else p.delete('after');
    setParams(p, { replace: false });
  };
  return {
    cursor,
    page: cursors.length + 1,
    older: (nextCursor: string) => go([...cursors, nextCursor]),
    newer: () => go(cursors.slice(0, -1)),
    reset: () => go([]),
  };
}

/** GitHub Actions jobs and API tasks: each ran, or runs, on a fresh Mac. */
export function CiPage() {
  useDocumentTitle('CI');
  const [params, setParams] = useSearchParams();
  const state = (params.get('state') as TaskState | null) ?? 'all';
  const pages = useCursorPages();
  const navigate = useNavigate();
  const now = useNow(1000);
  const capacity = useCapacity();

  const path = `/v1/tasks?limit=${PAGE}${state !== 'all' ? `&state=${state}` : ''}${pages.cursor ? `&cursor=${encodeURIComponent(pages.cursor)}` : ''}`;
  const anyLive = useRef(false);
  const res = useResource<{ tasks: TaskSummary[]; nextCursor: string | null }>(path, { interval: 5000 });
  const tasks = res.data?.tasks ?? [];
  anyLive.current = tasks.some((t) => !isTerminal(t.state));

  // A filter change starts again from the newest page.
  const setFilter = (next: TaskState | 'all') => setParams(next === 'all' ? {} : { state: next });
  const filtered = state !== 'all';
  const filterLabel = STATES.find((x) => x.value === state)?.label.toLowerCase() ?? '';

  return (
    <>
      <PageHead title="CI" lede="GitHub Actions jobs and API tasks, each on a fresh Mac, with the logs back." />
      <GitHubCard />
      <div className="toolbar">
        <Segmented label="Filter by state" options={STATES} value={state} onChange={setFilter} />
        <span className="toolbar__meta">
          <CapacityMeta cap={capacity.data} />
        </span>
      </div>
      <CapacityWarning cap={capacity.data} which="tasks" />
      {res.error ? (
        <div className="band">
          <ErrorCallout error={res.error} onRetry={() => void res.reload()} />
        </div>
      ) : null}
      <div className="band band--flush" style={{ paddingTop: 18 }}>
        {!res.loading && tasks.length === 0 && !res.error ? (
          !filtered && pages.page === 1 ? (
            <Empty title="Nothing has run yet">
              <p>GitHub jobs and tasks sent with <code>POST /v1/tasks</code> show up here with their logs.</p>
              <Link className="btn mt-8" to="/docs#tasks">
                How to send a task
              </Link>
            </Empty>
          ) : (
            <Empty title={`Nothing ${filterLabel}`}>
              <p>Nothing matches this filter{pages.page > 1 ? ' on this page' : ''}.</p>
              <button type="button" className="btn" onClick={() => setFilter('all')}>
                Show all
              </button>
            </Empty>
          )
        ) : (
          <div className="table-wrap">
            <table className={`table${res.refreshing && res.loading ? ' table--loading' : ''}`}>
              <thead>
                <tr>
                  <th scope="col">Repo</th>
                  <th scope="col">State</th>
                  <th scope="col">Created</th>
                  <th scope="col" className="r">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {res.loading && !res.data ? (
                  <SkeletonRows cols={4} />
                ) : (
                  tasks.map((task) => (
                    <tr key={task.id} className="is-link" onClick={() => navigate(`/ci/${task.id}`)}>
                      <td className="m-title" style={{ maxWidth: 320 }}>
                        <span className="task-name">
                          <Link className="task-repo" to={`/ci/${task.id}`} title={task.spec.repo} onClick={(e) => e.stopPropagation()}>
                            {repoName(task.spec.repo)}
                          </Link>
                          {task.github ? (
                            <span className="task-job" title={`${task.github.workflowName} / ${task.github.jobName}`}>
                              {task.github.jobName}
                            </span>
                          ) : null}
                          {task.replayUrl ? <ReplayChip id={task.id} live={!isTerminal(task.state)} /> : null}
                        </span>
                      </td>
                      <td className="m-end" title={failureLabel(task) ?? undefined}>
                        <WorkState task={task} />
                        {task.state === 'failed' && !task.github?.conclusion && failureLabel(task) ? <ReasonText label={failureLabel(task)!} /> : null}
                      </td>
                      <td className="m-meta m-inline nowrap" title={fullTime(task.createdAt)}>
                        {ago(task.createdAt, now)}
                      </td>
                      <td className="r num m-meta" data-label="Cost">
                        {moneyFine(task.costUsd)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {(pages.page > 1 || res.data?.nextCursor) && tasks.length > 0 ? (
          <div className="pager">
            <span>
              Page {pages.page}
              {filtered ? `, ${filterLabel} only` : ''}
            </span>
            <div className="pager__btns">
              <button type="button" className="btn btn--sm" disabled={pages.page === 1} onClick={pages.newer}>
                <ChevronLeft aria-hidden="true" />
                Newer
              </button>
              <button type="button" className="btn btn--sm" disabled={!res.data?.nextCursor} onClick={() => res.data?.nextCursor && pages.older(res.data.nextCursor)}>
                Older
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- detail */

const EVENT_LABEL: Record<TaskEvent['type'], string> = {
  claimed: 'Claimed by a box',
  started: 'Started',
  log: 'Log',
  artifact: 'Artifact uploaded',
  succeeded: 'Succeeded',
  failed: 'Failed',
  timed_out: 'Timed out',
  cancelled: 'Cancelled',
};

const EVENT_LAMP: Record<TaskEvent['type'], Parameters<typeof Lamp>[0]['tone']> = {
  claimed: 'amber',
  started: 'amber',
  log: 'off',
  artifact: 'accent',
  succeeded: 'accent',
  failed: 'refused',
  timed_out: 'magenta',
  cancelled: 'off',
};

function LogViewer({ task }: { task: Task }) {
  const all = task.events.filter((e) => e.type === 'log');
  // When the plane trimmed the front of the log, its first line is a marker, not output.
  const dropped = task.droppedLogLines ?? 0;
  const marker = dropped > 0 && all[0]?.data?.startsWith('…') ? all[0] : null;
  const logs = marker ? all.slice(1) : all;
  const box = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const t0 = task.startedAt ?? task.createdAt;
  const text = logs.map((l) => l.data ?? '').join('\n');

  useEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [logs.length]);

  const download = () => {
    const blob = new Blob([text + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${task.id}.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="panel" aria-label="Logs">
      <div className="panel__bar">
        <span className="panel__title">
          Logs{' '}
          <span style={{ color: 'var(--panel-ink-3)' }}>
            {dropped > 0 ? `last ${logs.length} of ${(dropped + logs.length).toLocaleString('en-US')} lines` : `${logs.length} lines`}
            {task.state === 'running' ? ', live' : ''}
          </span>
        </span>
        <span className="row" style={{ gap: 6 }}>
          <CopyAction text={text} label="Copy" size="sm" />
          <button type="button" className="btn btn--sm" onClick={download} disabled={logs.length === 0}>
            <Download aria-hidden="true" />
            Download
          </button>
        </span>
      </div>
      <div
        className="panel__scroll log"
        ref={box}
        tabIndex={0}
        aria-label="Log lines"
        style={{ maxHeight: 420, padding: '12px 0 14px' }}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
      >
        {marker ? (
          <div className="log__row is-note">
            <span className="log__n">…</span>
            <span className="log__t" />
            <span className="log__msg">{dropped.toLocaleString('en-US')} earlier lines weren’t kept. The plane keeps a task’s last 500.</span>
          </div>
        ) : null}
        {logs.length === 0 ? (
          <div className="log__empty">{isTerminal(task.state) ? (task.startedAt === undefined ? 'It never started, so there’s no log.' : 'This task logged nothing.') : task.state === 'running' ? 'Waiting for the first line…' : 'Logs start when a box starts the task.'}</div>
        ) : (
          logs.map((l, i) => {
            const msg = l.data ?? '';
            const err = /\berror\b|FAILED|fatal:/i.test(msg);
            const ok = /SUCCEEDED|Build complete|passed/.test(msg) && !err;
            return (
              <div key={i} className={`log__row${err ? ' is-err' : ok ? ' is-ok' : ''}`}>
                <span className="log__n">{dropped + i + 1}</span>
                <span className="log__t">+{duration(Math.max(0, l.at - t0))}</span>
                <span className="log__msg">{msg}</span>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

/** What a failed or timed-out task's reason means, and what to do about it. */
function FailureCallout({ task, uploaded, maxTimeoutMs }: { task: Task; uploaded: Set<string>; maxTimeoutMs?: number }) {
  const queueWait = span(task.spec.queueTimeoutMs ?? 15 * 60_000);
  const notBilled = ' You’re not billed for it.';
  // A GitHub job is never submitted here: it runs again when it is re-run on GitHub.
  const runner = task.spec.kind === 'runner';
  const again = runner ? ' Re-run the job on GitHub.' : ' Submit it again.';
  switch (task.failureReason) {
    case 'exit':
      if (runner) {
        return (
          <Callout tone="error" title={`GitHub’s runner exited ${task.exitCode ?? 'non-zero'}`}>
            The job’s own log is on GitHub; the log here is the runner’s.
          </Callout>
        );
      }
      return (
        <Callout tone="error" title={`The command exited ${task.exitCode ?? 'non-zero'}`}>
          It ran on a Mac and failed. The last lines of the log say why.
        </Callout>
      );
    case 'setup': {
      if (task.exitCode === 0) {
        const missing = task.spec.artifacts.filter((name) => !uploaded.has(name));
        return (
          <Callout tone="error" title="It exited 0 without its artifacts">
            A task only succeeds when every artifact it promised is a file in the repo root when the command ends. Missing: {missing.length ? missing.map((name, i) => <span key={name}>{i ? ', ' : ''}<code>{name}</code></span>) : 'none it reported'}.
          </Callout>
        );
      }
      if (runner) {
        return (
          <Callout tone="error" title="The Mac couldn’t start GitHub’s runner">
            The log has the details.{again}
          </Callout>
        );
      }
      return (
        <Callout tone="error" title="The Mac couldn’t clone the repo or start the command">
          Check that the repo is public (private repos aren’t supported yet) and that the command exists on macOS. The log has the details.
        </Callout>
      );
    }
    case 'timeout':
      return (
        <Callout tone="warn" title={task.spec.timeoutMs ? `It ran past its ${span(task.spec.timeoutMs)} timeout` : 'It ran past its timeout'}>
          The Mac stopped it at the timeout, and it’s billed up to then.{' '}
          {runner ? (
            'A GitHub job runs up to an hour here, so split the work.'
          ) : (
            <>
              Give it a longer <code>timeoutMs</code>
              {maxTimeoutMs ? ` (up to ${span(maxTimeoutMs)} on your team)` : ''}, or split the work.
            </>
          )}
        </Callout>
      );
    case 'no-capacity':
      return (
        <Callout tone="warn" title="No Mac was free to take it">
          No {task.spec.session === 'gui' ? 'GUI' : 'headless'} slot took it within its {queueWait} queue timeout, so it never ran.{notBilled}
          {task.spec.session === 'gui' ? ' GUI work needs a slot with a logged-in desktop.' : ''}
          {runner ? (
            again
          ) : (
            <>
              {' '}
              Submit it again, or give it a longer <code>queueTimeoutMs</code>.
            </>
          )}
        </Callout>
      );
    case 'box-lost':
      return (
        <Callout tone="info" title="The fleet lost the Mac running it">
          Its box stopped reporting mid-run, so there’s no result.{notBilled}
          {again}
        </Callout>
      );
    case 'plane-restart':
      return (
        <Callout tone="info" title={task.startedAt === undefined ? 'The plane restarted before a Mac took it' : 'The plane restarted while it ran'}>
          {task.startedAt === undefined ? (runner ? 'GitHub’s runner couldn’t be registered again.' : 'It never ran.') : 'Its result couldn’t be collected.'}
          {notBilled}
          {again}
        </Callout>
      );
    case 'never-started':
      return (
        <Callout tone="info" title="No box managed to start it">
          Boxes claimed it {task.attempts === 1 ? 'once' : `${task.attempts} times`} and none started it.{notBilled}
          {again}
        </Callout>
      );
    default:
      return null;
  }
}

function noExitCode(task: Task): string {
  if (!isTerminal(task.state)) return task.spec.kind === 'runner' ? 'When GitHub’s runner ends' : 'When the command ends';
  switch (task.failureReason) {
    case 'no-capacity':
    case 'never-started':
      return 'None: it never ran';
    case 'box-lost':
    case 'plane-restart':
      return 'None: its result was lost';
    case 'timeout':
      return 'None: stopped at its timeout';
    case 'setup':
      return 'None: the command never started';
    default:
      return task.state === 'cancelled' ? 'None: cancelled before it ended' : 'None: the command never finished';
  }
}

export function TaskDetail() {
  const { id = '' } = useParams();
  useDocumentTitle(shortId(id));
  const now = useNow(1000);
  const [live, setLive] = useState(true);
  const res = useResource<Task>(`/v1/tasks/${encodeURIComponent(id)}`, { interval: live ? 2000 : false });
  const team = useResource<TeamDetail>('/v1/team');
  const task = res.data;
  const cancel = useAction(async () => api<Task>(`/v1/tasks/${encodeURIComponent(id)}/cancel`, { method: 'POST' }));
  useEffect(() => {
    if (task && isTerminal(task.state)) setLive(false);
  }, [task]);

  const timeline = useMemo(() => (task ? task.events.filter((e) => e.type !== 'log') : []), [task]);
  const uploaded = useMemo(() => new Set(task?.events.filter((e) => e.type === 'artifact').map((e) => e.data ?? '') ?? []), [task]);

  if (res.error && !task) {
    return (
      <>
        <PageHead title="Task" crumbs={<Link to="/ci">CI</Link>} />
        <div className="band">
          {res.error.status === 404 ? (
            <Empty title="No such task">
              <p>
                <code>{id}</code> doesn’t exist on this team, or belongs to another one.
              </p>
              <Link className="btn" to="/ci">
                Back to CI
              </Link>
            </Empty>
          ) : (
            <ErrorCallout error={res.error} onRetry={() => void res.reload()} />
          )}
        </div>
      </>
    );
  }

  const ran = task ? taskDuration(task, now) : null;
  const artifacts = task ? [...new Set([...task.spec.artifacts, ...uploaded])] : [];
  const job = task?.github;
  const replay = task?.replayUrl ? <ReplayLink id={task.id} live={!isTerminal(task.state)} /> : null;
  const cancelButton =
    task && !isTerminal(task.state) ? (
      <InlineConfirm
        trigger="Cancel task"
        triggerClass="btn btn--danger"
        question={task.state === 'queued' ? 'Cancel it? A queued task is never billed.' : 'Cancel it? It’s billed up to now.'}
        confirmLabel="Cancel task"
        dismissLabel={task.state === 'queued' ? 'Keep it queued' : 'Keep running'}
        busy={cancel.busy}
        onConfirm={async () => {
          const done = await cancel.run();
          if (done) res.setData(done);
          else void res.reload();
        }}
      />
    ) : null;

  return (
    <>
      <PageHead
        crumbs={
          <>
            <Link to="/ci">CI</Link>
            <span aria-hidden="true">/</span>
            <span>{job ? repoName(task.spec.repo) : (task?.spec.kind ?? '…')}</span>
          </>
        }
        title={job ? job.jobName : task ? `${task.spec.kind} task` : 'Task'}
        actions={
          job || replay || cancelButton ? (
            <span className="row" style={{ gap: 8 }}>
              {replay}
              {job ? (
                <a className="btn" href={job.runUrl} target="_blank" rel="noreferrer">
                  View on GitHub
                  <ExternalLink aria-hidden="true" />
                </a>
              ) : null}
              {cancelButton}
            </span>
          ) : undefined
        }
        lede={
          task ? (
            <span className="id-line">
              <code>{task.id}</code>
              <CopyButton text={task.id} label="Copy task id" />
            </span>
          ) : undefined
        }
      />
      {res.error ? (
        <div className="band band--tight">
          <ErrorCallout error={res.error} onRetry={() => void res.reload()} />
        </div>
      ) : null}
      {cancel.error && cancel.error.code !== 'already-ended' ? (
        <div className="band band--tight">
          <ErrorCallout error={cancel.error} />
        </div>
      ) : null}
      <div className="stats">
        <div className="stat">
          <div className="stat__label">State</div>
          {task ? <WorkState task={task} /> : <span className="skeleton" style={{ width: 90, marginTop: 14 }} />}
          {task && !isTerminal(task.state) ? <div className="stat__sub">Refreshing every 2 s</div> : null}
          {task && failureLabel(task) ? (
            <div className="stat__sub">{failureLabel(task)}</div>
          ) : task && isTerminal(task.state) && task.exitCode !== null ? (
            <div className="stat__sub mono">exit {task.exitCode}</div>
          ) : null}
        </div>
        <div className="stat">
          <div className="stat__label">Ran for</div>
          <div className="stat__value">{task ? duration(ran) : '—'}</div>
          {task?.spec.timeoutMs ? <div className="stat__sub">of a {span(task.spec.timeoutMs)} timeout</div> : null}
        </div>
        <div className="stat">
          <div className="stat__label">Cost</div>
          <div className="stat__value">{task ? moneyFine(task.costUsd) : '—'}</div>
          <div className="stat__sub">{task ? costNote(task) : ''}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Created by</div>
          <div className="stat__value" style={{ fontSize: '1.0625rem', marginTop: 12 }}>
            {task ? createdByLabel(task) : '—'}
          </div>
          <div className="stat__sub" title={task ? fullTime(task.createdAt) : undefined}>
            {task ? ago(task.createdAt, now) : ''}
          </div>
        </div>
      </div>

      {task?.failureReason ? (
        <div className="band band--tight">
          <FailureCallout task={task} uploaded={uploaded} maxTimeoutMs={team.data?.limits.maxTaskTimeoutMs} />
        </div>
      ) : null}
      <div className="split split--aside">
        <div>
          <section className="band">{task ? <LogViewer task={task} /> : <span className="skeleton" style={{ height: 200 }} />}</section>
          <section className="band">
            <div className="band__head">
              <h2 className="h2">Spec</h2>
            </div>
            {task ? (
              <dl className="spec">
                <div>
                  <dt>Repo</dt>
                  <dd>
                    <a href={task.spec.repo} target="_blank" rel="noreferrer">
                      {task.spec.repo}
                    </a>
                  </dd>
                </div>
                {job ? (
                  <div>
                    <dt>Job</dt>
                    <dd>
                      <a href={job.runUrl} target="_blank" rel="noreferrer">
                        {job.workflowName} / {job.jobName}
                      </a>
                    </dd>
                  </div>
                ) : (
                  <div>
                    <dt>{task.spec.kind === 'agent' ? 'Agent' : 'Command'}</dt>
                    <dd>
                      <code>{task.spec.kind === 'agent' ? task.spec.agent : task.spec.command}</code>
                    </dd>
                  </div>
                )}
                <div>
                  <dt>Kind</dt>
                  <dd>{task.spec.kind}</dd>
                </div>
                <div>
                  <dt>Session</dt>
                  <dd>{task.spec.session === 'gui' ? 'GUI, in a logged-in desktop session' : 'Headless, no desktop'}</dd>
                </div>
                <div>
                  <dt>Timeout</dt>
                  <dd>
                    {task.spec.timeoutMs ? span(task.spec.timeoutMs) : '30 min (default)'}
                    <span className="muted"> to run, {task.spec.queueTimeoutMs ? span(task.spec.queueTimeoutMs) : '15 min'} to wait for a box</span>
                  </dd>
                </div>
                <div>
                  <dt>Box</dt>
                  <dd>
                    {task.boxId ? (
                      <span className="mono small" title={task.boxId}>
                        {shortId(task.boxId)} slot {task.slot}
                      </span>
                    ) : (
                      <span className="muted">{isTerminal(task.state) ? 'Never claimed' : 'Not claimed yet'}</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Exit code</dt>
                  <dd>
                    {task.exitCode !== null ? <code>{task.exitCode}</code> : <span className="muted">{noExitCode(task)}</span>}
                  </dd>
                </div>
                {/* attempts counts claims a box abandoned without starting (each requeues the task), so 0 is normal. */}
                {task.attempts > 0 ? (
                  <div>
                    <dt>Requeued</dt>
                    <dd>
                      {task.attempts === 1 ? '1 time' : `${task.attempts} times`}
                      <span className="muted">: a box claimed it and didn’t start it</span>
                    </dd>
                  </div>
                ) : null}
                <div>
                  <dt>Times</dt>
                  <dd className="small">
                    Created {fullTime(task.createdAt)}
                    {task.startedAt ? (
                      <>
                        <br />
                        Started {fullTime(task.startedAt)}
                      </>
                    ) : null}
                    {task.endedAt ? (
                      <>
                        <br />
                        Ended {fullTime(task.endedAt)}
                      </>
                    ) : null}
                  </dd>
                </div>
              </dl>
            ) : (
              <div className="stack">
                {[70, 50, 40, 60].map((w) => (
                  <span key={w} className="skeleton" style={{ width: `${w}%` }} />
                ))}
              </div>
            )}
          </section>
        </div>
        <aside>
          <section className="band">
            <div className="band__head">
              <h2 className="h2">Timeline</h2>
            </div>
            {task ? (
              <ol className="timeline">
                <li>
                  <Lamp tone="hollow" />
                  <div>
                    <div className="timeline__what">Queued</div>
                  </div>
                  <time title={fullTime(task.createdAt)}>{clock(task.createdAt)}</time>
                </li>
                {timeline.map((e, i) => (
                  <li key={i}>
                    <Lamp tone={EVENT_LAMP[e.type]} />
                    <div style={{ minWidth: 0 }}>
                      <div className="timeline__what">{EVENT_LABEL[e.type]}</div>
                      {e.data ? <span className="timeline__data">{e.type === 'claimed' ? e.data.replace(/^(box_\w{6})\w+/, '$1…') : e.data}</span> : null}
                    </div>
                    <time title={fullTime(e.at)}>+{duration(e.at - task.createdAt)}</time>
                  </li>
                ))}
                {!isTerminal(task.state) ? (
                  <li>
                    <Lamp tone="live" />
                    <div className="timeline__what muted">{task.state === 'queued' ? 'Waiting for a free slot' : task.state === 'claimed' ? 'Starting' : 'Running'}</div>
                    <time>now</time>
                  </li>
                ) : null}
              </ol>
            ) : null}
          </section>
          {job ? null : (
            <section className="band">
              <div className="band__head">
                <h2 className="h2">Artifacts</h2>
              </div>
              {task && artifacts.length === 0 ? <p className="muted small" style={{ margin: 0 }}>This task promised no artifacts.</p> : null}
              {task ? (
                <ul className="files">
                  {artifacts.map((name) => {
                    const has = uploaded.has(name);
                    return (
                      <li key={name}>
                        <div style={{ minWidth: 0 }}>
                          <span className="mono small" style={{ overflowWrap: 'anywhere' }}>
                            {name}
                          </span>
                          <span className="cell-sub">{has ? 'Uploaded' : isTerminal(task.state) ? 'Not uploaded' : 'Not uploaded yet'}</span>
                        </div>
                        {has ? (
                          <a className="btn btn--sm" href={`/v1/tasks/${encodeURIComponent(task.id)}/artifacts/${encodeURIComponent(name)}`} download={name}>
                            <Download aria-hidden="true" />
                            Download
                          </a>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </section>
          )}
        </aside>
      </div>
    </>
  );
}
